import React from "react";
import { Check, Loader2, Circle } from "lucide-react";

/**
 * V1.5.3 — Build Plan Preview Badge.
 * Cyan, non-intrusive header that appears above the chat input when the
 * backend returns a `build_queue` for a multi-zone prompt. Phases tick
 * forward as the merge engine completes each zone (driven by the parent's
 * isThinking + auto_continue chain).
 *
 * Status legend:
 *   • "completed"   — green check (✓)
 *   • "in_progress" — cyan animated spinner
 *   • "pending"     — dim ring
 */
export default function BuildPlanBadge({ queue, isThinking }) {
  if (!Array.isArray(queue) || queue.length === 0) return null;

  return (
    <div
      data-testid="build-plan-badge"
      className="px-4 py-2.5 border-t border-brass-900/60 bg-brass-950/20 backdrop-blur-sm"
    >
      <div className="flex items-center gap-2 mb-1.5">
        <span className="relative flex h-2 w-2" aria-hidden="true">
          {isThinking && (
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brass-400 opacity-75" />
          )}
          <span className="relative inline-flex rounded-full h-2 w-2 bg-brass-400" />
        </span>
        <span
          className="font-mono text-[9px] uppercase tracking-widest text-brass-300"
          data-testid="build-plan-label"
        >
          // Build Plan · {queue.length} {queue.length === 1 ? "phase" : "phases"}
        </span>
      </div>
      <ol
        className="flex items-center gap-1.5 flex-wrap font-mono text-[10px] text-zinc-300"
        data-testid="build-plan-queue"
      >
        {queue.map((phase, i) => {
          const isLast = i === queue.length - 1;
          const status = phase.status || "pending";
          let icon;
          let chipCls;
          if (status === "completed") {
            icon = <Check className="w-3 h-3 text-emerald-400" />;
            chipCls = "border-emerald-500/40 bg-emerald-950/30 text-emerald-200";
          } else if (status === "in_progress") {
            icon = <Loader2 className="w-3 h-3 text-brass-300 animate-spin" />;
            chipCls = "border-brass-400/60 bg-brass-950/40 text-brass-100";
          } else {
            icon = <Circle className="w-3 h-3 text-zinc-600" />;
            chipCls = "border-zinc-800 bg-zinc-900/40 text-zinc-500";
          }
          return (
            <React.Fragment key={`${i}-${phase.label}`}>
              <li
                className={`flex items-center gap-1.5 px-2 py-1 border ${chipCls} transition-colors duration-300`}
                data-testid={`build-plan-phase-${i}`}
                data-phase-status={status}
                data-phase-label={phase.label}
              >
                {icon}
                <span className="font-mono text-[10px] uppercase tracking-wider">
                  Phase {i + 1}: {phase.label}
                </span>
              </li>
              {!isLast && (
                <span className="text-zinc-600 select-none" aria-hidden="true">
                  →
                </span>
              )}
            </React.Fragment>
          );
        })}
      </ol>
    </div>
  );
}
